// ============================================================
//  ALTIMO Medical Center — Reportes.jsx  (v2)
//  Secciones:
//    1. Diario     — ingresos del día + alertas colapsables
//    2. Mensual    — facturación día a día con totales
//    3. Facturación — listado por rango, Excel + PDF
//    4. Inventario — valor en bodega, rotación, Excel + PDF
//    5. Kardex     — movimientos por producto, Excel + PDF
//    6. Controlados— despachos de med. controlados, Excel + PDF
//  Exportación: Excel via CSV nativo, PDF via ventana de impresión
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';

// ── Paleta ───────────────────────────────────────────────────
const C = {
  azul:    '#1B2A5E', verde:   '#1D9E75', rojo:    '#dc3545',
  naranja: '#e67e22', gris:    '#6c757d', morado:  '#6f42c1',
  amarillo:'#856404',
};

const S = {
  pagina:  { background:'#f0f4f8', minHeight:'100vh', padding:'20px', fontFamily:'Segoe UI, sans-serif' },
  card:    { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', marginBottom:'16px' },
  cardTit: {
    fontSize:'12px', fontWeight:'700', color:C.azul, textTransform:'uppercase',
    letterSpacing:'.05em', marginBottom:'14px', paddingBottom:'10px',
    borderBottom:'2px solid #e8f0fb', display:'flex', justifyContent:'space-between', alignItems:'center',
  },
  fila2:   { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' },
  tabla:   { width:'100%', borderCollapse:'collapse', fontSize:'13px' },
  th:      { textAlign:'left', padding:'8px 10px', fontSize:'11px', color:C.gris, fontWeight:'600',
             borderBottom:'2px solid #e8f0fb', background:'#f8fafd', whiteSpace:'nowrap' },
  td:      { padding:'8px 10px', borderBottom:'1px solid #eef1f6', color:'#333', fontSize:'12px' },
  input:   { fontSize:'13px', padding:'7px 10px', border:'1px solid #c0ccda', borderRadius:'7px',
             background:'#fff', color:'#333', outline:'none' },
  label:   { display:'block', fontSize:'11px', fontWeight:'600', color:C.gris,
             marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.04em' },
  campo:   { marginBottom:'0' },
  btnAzul: { background:C.azul,   color:'#fff', border:'none', borderRadius:'7px',
             padding:'8px 18px', fontSize:'13px', fontWeight:'700', cursor:'pointer' },
  btnVerde:{ background:C.verde,  color:'#fff', border:'none', borderRadius:'7px',
             padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnGris: { background:'#fff', color:'#495057', border:'1px solid #c0ccda',
             borderRadius:'7px', padding:'7px 14px', fontSize:'12px', cursor:'pointer' },
  btnNar:  { background:C.naranja,color:'#fff', border:'none', borderRadius:'7px',
             padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  btnMor:  { background:C.morado, color:'#fff', border:'none', borderRadius:'7px',
             padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' },
  totalBox:{ background:C.azul, borderRadius:'10px', padding:'14px 20px',
             display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'10px' },
  barraWrap:{ background:'#e8f0fb', borderRadius:'99px', height:'10px', flex:1, margin:'0 10px' },
  barra:(p,c)=>({ background:c, height:'10px', borderRadius:'99px',
                  width:`${Math.min(p,100)}%`, transition:'width .4s' }),
  alerta:(t)=>({
    padding:'10px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:'500',
    background:t==='err'?'#f8d7da':t==='ok'?'#d4edda':'#fff3cd',
    color:t==='err'?'#721c24':t==='ok'?'#155724':'#856404',
    border:`1px solid ${t==='err'?'#f5c6cb':t==='ok'?'#c3e6cb':'#ffc107'}`,
    marginBottom:'12px',
  }),
  badge:(c)=>({
    display:'inline-block', padding:'2px 8px', borderRadius:'99px',
    fontSize:'10px', fontWeight:'700', background:c+'22', color:c, border:`1px solid ${c}44`,
  }),
  tab:(a)=>({
    padding:'9px 20px', borderRadius:'8px', fontSize:'13px', fontWeight:'600',
    cursor:'pointer', border:'none',
    background:a?C.azul:'#e8f0fb', color:a?'#fff':C.azul,
  }),
};

const TIPO_COLOR = {
  Habitacion:'#1B2A5E', Medicamento:'#1D9E75', Procedimiento:'#7F77DD',
  Examen:'#BA7517', Honorario:'#D4537E', Insumo:'#0F6E56', Otro:'#888780',
};

// ── Helpers ──────────────────────────────────────────────────
const hoy  = () => new Date().toISOString().slice(0,10);
const mesA = () => { const d=new Date(); return { anio:d.getFullYear(), mes:d.getMonth()+1 }; };
const fmt  = (f) => f ? new Date(f+'T00:00:00').toLocaleDateString('es-SV') : '—';
const fmtDT= (f) => f ? new Date(f).toLocaleString('es-SV') : '—';
const mon  = (n) => `$${Number(n||0).toFixed(2)}`;
const MESES= ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

// ── Primer día del mes actual ────────────────────────────────
const primerDiaMes = () => {
  const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
};

// ════════════════════════════════════════════════════════════
//  EXPORTADORES
// ════════════════════════════════════════════════════════════

/** Exporta array de objetos a CSV y descarga */
function exportarCSV(datos, columnas, nombreArchivo) {
  const enc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const cabecera = columnas.map(c => enc(c.label)).join(',');
  const filas    = datos.map(row =>
    columnas.map(c => enc(row[c.key])).join(',')
  );
  const csv = [cabecera, ...filas].join('\n');
  const blob = new Blob(['\uFEFF'+csv], { type:'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `${nombreArchivo}.csv`; a.click();
  URL.revokeObjectURL(url);
}

/** Imprime HTML en ventana nueva → PDF del sistema */
function imprimirHTML(html, titulo) {
  const w = window.open('','_blank','width=900,height=700');
  w.document.write(`<!DOCTYPE html><html lang="es"><head>
    <meta charset="UTF-8"/>
    <title>${titulo}</title>
    <style>
      * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family:Segoe UI,sans-serif; padding:28px 32px; color:#222; font-size:12px; }
      h1 { color:#1B2A5E; font-size:17px; margin-bottom:4px; }
      .sub { color:#6c757d; font-size:11px; margin-bottom:16px; }
      .linea { height:2px; background:linear-gradient(90deg,#1B2A5E,#1D9E75); margin-bottom:16px; }
      table { width:100%; border-collapse:collapse; font-size:11px; }
      th { background:#1B2A5E; color:#fff; padding:7px 8px; text-align:left; font-weight:600; }
      td { padding:6px 8px; border-bottom:1px solid #eee; }
      tr:nth-child(even) td { background:#f8fafd; }
      .total-box { background:#1B2A5E; color:#fff; padding:10px 16px; margin-top:12px;
                   border-radius:6px; display:flex; justify-content:space-between; }
      .total-box span:last-child { font-size:16px; font-weight:800; }
      .badge-ctrl { background:#f3e5f5; color:#6a1b9a; border:1px solid #ce93d8;
                    padding:1px 6px; border-radius:99px; font-size:9px; font-weight:700; margin-left:4px; }
      @media print { body { padding:12px; } }
    </style>
  </head><body>
    <h1>ALTIMO Medical Center</h1>
    <div class="sub">Generado: ${new Date().toLocaleString('es-SV')}</div>
    <div class="linea"></div>
    ${html}
    <script>window.onload=()=>{ setTimeout(()=>window.print(),400); }<\/script>
  </body></html>`);
  w.document.close();
}

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function Reportes({ usuario }) {
  const [tab, setTab] = useState('diario');
  const soloLectura = usuario?.rol === 'auditoria';
  const verGerencial = ['admin','contaduria','auditoria'].includes(usuario?.rol);

  const TABS = [
    { id:'diario',       label:'📅 Diario'        },
    { id:'mensual',      label:'📆 Mensual'        },
    ...(verGerencial ? [{ id:'gerencial', label:'📈 Gerencial' }] : []),
    { id:'facturacion',  label:'🧾 Facturación'    },
    { id:'contabilidad', label:'💰 Contabilidad'   },
  ];

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {/* ══ Header módulo — estandarizado ══ */}
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px 0' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          📊 Módulo de Reportes
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)', marginBottom:'12px' }}>
          Caja diaria · Mensual · Facturación · Contabilidad
        </div>
        <div style={{ display:'flex', gap:'2px', flexWrap:'wrap' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:'9px 18px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', fontFamily:'inherit',
                background: tab === t.id ? '#fff' : 'rgba(255,255,255,.15)',
                color:      tab === t.id ? '#1B2A5E' : '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={S.pagina}>

      {soloLectura && (
        <div style={{ ...S.alerta('warn'), display:'flex', alignItems:'center', gap:'8px' }}>
          <span>🔒</span>
          <span>Modo auditoría: acceso de solo lectura. Exportación a Excel/PDF disponible para consulta.</span>
        </div>
      )}

      {tab === 'diario'       && <SeccionDiario    usuario={usuario} />}
      {tab === 'mensual'      && <SeccionMensual   usuario={usuario} />}
      {tab === 'gerencial'    && verGerencial && <SeccionGerencial usuario={usuario} />}
      {tab === 'facturacion'  && <SeccionFacturacion usuario={usuario} />}
      {tab === 'contabilidad' && <SeccionContabilidad usuario={usuario} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  1. DIARIO
// ════════════════════════════════════════════════════════════
function SeccionDiario({ usuario }) {
  const [fecha,       setFecha]       = useState(hoy());
  const [reporte,     setReporte]     = useState(null);
  const [alertas,     setAlertas]     = useState(null);
  const [alertasOpen, setAlertasOpen] = useState(false);
  const [cargando,    setCargando]    = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    const [rD, rA] = await Promise.all([
      api.reportes.dia({ fecha }),
      api.reportes.alertas(),
    ]);
    if (rD.ok) setReporte(rD.data);
    if (rA.ok) setAlertas(rA.data);
    setCargando(false);
  }, [fecha]);

  useEffect(() => { cargar(); }, []);

  const maxCat = reporte ? Math.max(...(reporte.porCategoria?.map(c=>c.total)||[1]),1) : 1;
  const totalAlertas = alertas
    ? (alertas.stockBajo?.length||0)+(alertas.porVencer?.length||0)+(alertas.cuentasSinFacturar?.length||0)
    : 0;

  const imprimirDia = () => {
    if (!reporte) return;
    const filas = reporte.porCategoria.map(c =>
      `<tr><td>${c.tipo}</td><td style="text-align:right;font-weight:700">${mon(c.total)}</td></tr>`
    ).join('');
    imprimirHTML(`
      <h2 style="margin-bottom:10px">Reporte diario — ${fmt(fecha)}</h2>
      <table><thead><tr><th>Categoría</th><th>Total</th></tr></thead>
      <tbody>${filas}</tbody></table>
      <div class="total-box" style="margin-top:12px">
        <span>Total facturado</span><span>${mon(reporte.totalFacturado)}</span>
      </div>
      <p style="color:#6c757d;font-size:11px;margin-top:10px">
        Cuentas abiertas: <strong>${reporte.cuentasAbiertas}</strong>
      </p>
    `, `Reporte Diario ${fecha}`);
  };

  return (
    <>
      <div style={S.card}>
        <div style={S.cardTit}>
          <span>Reporte diario</span>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <input type="date" style={S.input} value={fecha} onChange={e=>setFecha(e.target.value)} />
            <button style={S.btnAzul} onClick={cargar}>Cargar</button>
            {reporte && <button style={S.btnVerde} onClick={imprimirDia}>🖨 PDF</button>}
          </div>
        </div>

        {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Cargando...</div>}

        {reporte && !cargando && (
          <>
            {reporte.porCategoria?.length === 0
              ? <p style={{ color:C.gris, fontSize:'13px' }}>Sin movimientos para esta fecha.</p>
              : <>
                  {reporte.porCategoria?.map(cat => (
                    <div key={cat.tipo} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #eef1f6' }}>
                      <span style={{ minWidth:'130px', fontWeight:'500' }}>{cat.tipo}</span>
                      <div style={S.barraWrap}>
                        <div style={S.barra((cat.total/maxCat)*100, TIPO_COLOR[cat.tipo]||C.azul)} />
                      </div>
                      <span style={{ fontWeight:'700', color:C.azul, minWidth:'80px', textAlign:'right' }}>
                        {mon(cat.total)}
                      </span>
                    </div>
                  ))}
                  <div style={S.totalBox}>
                    <span style={{ color:'#fff', fontSize:'13px' }}>Total facturado</span>
                    <span style={{ color:'#fff', fontSize:'20px', fontWeight:'800' }}>
                      {mon(reporte.totalFacturado)}
                    </span>
                  </div>
                  <div style={{ fontSize:'12px', color:C.gris, marginTop:'10px' }}>
                    Cuentas aún abiertas: <strong>{reporte.cuentasAbiertas}</strong>
                  </div>
                </>
            }
          </>
        )}
      </div>

      {/* Alertas colapsables */}
      <div style={S.card}>
        <div
          style={{ ...S.cardTit, cursor:'pointer', marginBottom:alertasOpen?'14px':0,
                   paddingBottom:alertasOpen?'10px':0, borderBottom:alertasOpen?'2px solid #e8f0fb':'none' }}
          onClick={() => setAlertasOpen(a=>!a)}>
          <span style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            Alertas del sistema
            {totalAlertas > 0 && (
              <span style={{ background:'#dc3545', color:'#fff', borderRadius:'99px', padding:'1px 8px', fontSize:'10px', fontWeight:'700' }}>
                {totalAlertas}
              </span>
            )}
            {totalAlertas === 0 && alertas && (
              <span style={{ background:'#d4edda', color:'#155724', borderRadius:'99px', padding:'1px 8px', fontSize:'10px', fontWeight:'700' }}>✓</span>
            )}
          </span>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button style={S.btnAzul} onClick={e=>{ e.stopPropagation(); cargar(); }}>Actualizar</button>
            <span style={{ fontSize:'16px', color:C.gris }}>{alertasOpen ? '▲' : '▼'}</span>
          </div>
        </div>

        {alertasOpen && alertas && (
          <>
            {totalAlertas === 0 && <p style={{ color:C.verde, fontSize:'13px', fontWeight:'600' }}>✓ Sin alertas activas</p>}
            <AlertaSeccion titulo="Stock bajo" items={alertas.stockBajo} tipo="err"
              render={a=>`${a.nombre} — ${a.stock} ${a.unidad} (mín: ${a.stock_minimo})`} />
            <AlertaSeccion titulo="Por vencer" items={alertas.porVencer} tipo="warn"
              render={a=>`${a.nombre} [${a.numero_lote}] — Vence: ${fmt(a.fecha_vencimiento)} (${a.dias_restantes}d)`} />
            <AlertaSeccion titulo="Alta sin facturar" items={alertas.cuentasSinFacturar} tipo="warn"
              render={a=>`${a.paciente} — ${a.dias} días sin facturar`} />
          </>
        )}
        {!alertasOpen && alertas && (
          <div style={{ fontSize:'12px', color:totalAlertas>0?C.amarillo:C.verde, padding:'4px 0' }}>
            {totalAlertas > 0
              ? `Haz clic para ver ${totalAlertas} alerta${totalAlertas!==1?'s':''} activa${totalAlertas!==1?'s':''}.`
              : 'Sin alertas activas.'}
          </div>
        )}
      </div>
    </>
  );
}

function AlertaSeccion({ titulo, items, tipo, render }) {
  if (!items?.length) return null;
  const bg  = tipo==='err' ? '#f8d7da' : '#fff3cd';
  const col = tipo==='err' ? '#721c24' : '#856404';
  const bor = tipo==='err' ? '#f5c6cb' : '#ffc107';
  return (
    <>
      <div style={{ fontSize:'11px', fontWeight:'700', color:col, margin:'10px 0 6px', textTransform:'uppercase' }}>
        {titulo} ({items.length})
      </div>
      {items.map((a,i) => (
        <div key={i} style={{ background:bg, color:col, border:`1px solid ${bor}`,
          borderRadius:'8px', padding:'8px 12px', marginBottom:'6px', fontSize:'12px' }}>
          {render(a)}
        </div>
      ))}
    </>
  );
}

// ════════════════════════════════════════════════════════════
//  2. MENSUAL
// ════════════════════════════════════════════════════════════
function SeccionMensual() {
  const { anio:anioI, mes:mesI } = mesA();
  const [anio,    setAnio]    = useState(anioI);
  const [mes,     setMes]     = useState(mesI);
  const [datos,   setDatos]   = useState([]);
  const [cargando,setCargando]= useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    const r = await api.reportes.mes({ anio, mes });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  }, [anio, mes]);

  useEffect(() => { cargar(); }, []);

  const total = datos.reduce((s,r) => s+Number(r.total),0);
  const totalFact = datos.reduce((s,r) => s+Number(r.facturas),0);

  const imprimirMes = () => {
    const filas = datos.map(r =>
      `<tr><td>${fmt(r.fecha)}</td><td style="text-align:center">${r.facturas}</td>
       <td style="text-align:right;font-weight:700">${mon(r.total)}</td></tr>`
    ).join('');
    imprimirHTML(`
      <h2 style="margin-bottom:10px">Reporte mensual — ${MESES[mes-1]} ${anio}</h2>
      <table><thead><tr><th>Fecha</th><th>Facturas</th><th>Total</th></tr></thead>
      <tbody>${filas}</tbody></table>
      <div class="total-box">
        <span>${totalFact} facturas emitidas</span>
        <span>${mon(total)}</span>
      </div>
    `, `Reporte Mensual ${MESES[mes-1]} ${anio}`);
  };

  const exportarExcel = () => {
    exportarCSV(datos, [
      { key:'fecha',    label:'Fecha'    },
      { key:'facturas', label:'Facturas' },
      { key:'total',    label:'Total ($)'},
    ], `Mensual_${anio}_${String(mes).padStart(2,'0')}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Reporte mensual</span>
        <div style={{ display:'flex', gap:'8px' }}>
          <select style={S.input} value={mes} onChange={e=>setMes(Number(e.target.value))}>
            {MESES.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <input type="number" style={{ ...S.input, width:'80px' }} value={anio}
            onChange={e=>setAnio(Number(e.target.value))} />
          <button style={S.btnAzul} onClick={cargar}>Cargar</button>
          {datos.length>0 && <>
            <button style={S.btnVerde}  onClick={exportarExcel}>⬇ Excel</button>
            <button style={S.btnNar}    onClick={imprimirMes}>🖨 PDF</button>
          </>}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Cargando...</div>}

      {!cargando && datos.length === 0 && <p style={{ color:C.gris, fontSize:'13px' }}>Sin datos para este mes.</p>}

      {!cargando && datos.length > 0 && (
        <>
          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  <th style={S.th}>Fecha</th>
                  <th style={{ ...S.th, textAlign:'center' }}>Facturas</th>
                  <th style={{ ...S.th, textAlign:'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {datos.map((r,i) => (
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={S.td}>{fmt(r.fecha)}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{r.facturas}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>{totalFact} facturas — {MESES[mes-1]} {anio}</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{mon(total)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  2B. GERENCIAL — Médicos, Cirugías, Rentabilidad, Ocupación, Servicios
// ════════════════════════════════════════════════════════════
function SeccionGerencial() {
  const [sub, setSub] = useState('medicos');
  const SUBTABS = [
    { id:'medicos',      label:'👨‍⚕️ Médicos'        },
    { id:'cirugias',     label:'🔪 Cirugías'          },
    { id:'rentabilidad', label:'💹 Rentabilidad'      },
    { id:'ocupacion',    label:'🛏️ Ocupación'        },
    { id:'servicios',    label:'🏆 Top servicios'     },
  ];

  return (
    <div>
      <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
        {SUBTABS.map(t => (
          <button key={t.id}
            style={{ ...S.tab(sub===t.id), padding:'7px 14px', fontSize:'12px' }}
            onClick={() => setSub(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'medicos'      && <SubIngresosMedicos />}
      {sub === 'cirugias'     && <SubCirugiasDemandadas />}
      {sub === 'rentabilidad' && <SubRentabilidadTipo />}
      {sub === 'ocupacion'    && <SubOcupacionHabitaciones />}
      {sub === 'servicios'    && <SubTopServicios />}
    </div>
  );
}

// ── G.1 Ingresos y casos por médico ─────────────────────────
function SubIngresosMedicos() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [datos,   setDatos]   = useState([]);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.gerencialMedicos({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const totalGeneral = datos.reduce((s,d)=>s+Number(d.total_facturado),0);
  const totalCasos   = datos.reduce((s,d)=>s+Number(d.casos_atendidos),0);
  const maxFact      = Math.max(...datos.map(d=>d.total_facturado), 1);

  const COLS_CSV = [
    { key:'medico',                  label:'Médico'                },
    { key:'especialidad',            label:'Especialidad'          },
    { key:'casos_atendidos',         label:'Casos atendidos'       },
    { key:'facturas',                label:'Facturas'              },
    { key:'total_facturado',         label:'Total facturado ($)'   },
    { key:'ticket_promedio',         label:'Ticket promedio ($)'   },
    { key:'dias_estancia_promedio',  label:'Días estancia prom.'   },
  ];

  const imprimirPDF = () => {
    const filas = datos.map(d=>`<tr>
      <td>${d.medico}</td><td>${d.especialidad||'—'}</td>
      <td style="text-align:center">${d.casos_atendidos}</td>
      <td style="text-align:center">${d.facturas}</td>
      <td style="text-align:right">${mon(d.ticket_promedio)}</td>
      <td style="text-align:right;font-weight:700">${mon(d.total_facturado)}</td>
    </tr>`).join('');
    imprimirHTML(`
      <h2>Ingresos por médico</h2>
      <p style="color:#6c757d;margin-bottom:12px">${fmt(desde)} al ${fmt(hasta)} · ${datos.length} médicos con casos</p>
      <table><thead><tr>
        <th>Médico</th><th>Especialidad</th><th>Casos</th><th>Facturas</th><th>Ticket prom.</th><th>Total facturado</th>
      </tr></thead><tbody>${filas}</tbody></table>
      <div class="total-box">
        <span>${totalCasos} casos atendidos en el período</span>
        <span>${mon(totalGeneral)}</span>
      </div>
    `, `Ingresos_Medicos_${desde}_${hasta}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Ingresos y casos por médico</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {datos.length>0 && <>
            <button style={S.btnVerde} onClick={()=>exportarCSV(datos,COLS_CSV,`IngresosMedicos_${desde}_${hasta}`)}>⬇ Excel</button>
            <button style={S.btnNar}   onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      <div style={{ background:'#e8f0fb', borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', fontSize:'12px', color:C.azul }}>
        ℹ️ El vínculo médico–ingreso se basa en el médico tratante registrado en la admisión.
        Honorarios facturados dentro de esa admisión se atribuyen a dicho médico.
      </div>

      {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && datos.length===0 && (
        <p style={{ color:C.gris,fontSize:'13px' }}>Sin facturas asociadas a médicos en este período.</p>
      )}

      {!cargando && datos.length>0 && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'16px' }}>
            {[
              { label:'Médicos con casos', valor:datos.length,      color:C.azul   },
              { label:'Casos atendidos',   valor:totalCasos,        color:C.naranja},
              { label:'Total facturado',   valor:mon(totalGeneral), color:C.verde  },
            ].map(k=>(
              <div key={k.label} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{k.label}</div>
                <div style={{ fontSize:'16px', fontWeight:'800', color:k.color }}>{k.valor}</div>
              </div>
            ))}
          </div>

          {datos.map(d=>(
            <div key={d.medico_id} style={{ display:'flex', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #eef1f6' }}>
              <div style={{ minWidth:'220px' }}>
                <div style={{ fontWeight:'700', color:C.azul }}>{d.medico}</div>
                <div style={{ fontSize:'11px', color:C.gris }}>{d.especialidad||'—'} · {d.casos_atendidos} casos</div>
              </div>
              <div style={S.barraWrap}>
                <div style={S.barra((d.total_facturado/maxFact)*100, C.azul)} />
              </div>
              <span style={{ fontWeight:'700', color:C.azul, minWidth:'90px', textAlign:'right' }}>
                {mon(d.total_facturado)}
              </span>
            </div>
          ))}

          <div style={{ overflowX:'auto', marginTop:'16px' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Médico','Especialidad','Casos','Facturas','Ticket prom.','Días estancia','Total facturado'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map(d=>(
                  <tr key={d.medico_id}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul }}>{d.medico}</td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{d.especialidad||'—'}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{d.casos_atendidos}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{d.facturas}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(d.ticket_promedio)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{d.dias_estancia_promedio ?? '—'}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(d.total_facturado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── G.2 Cirugías más demandadas ──────────────────────────────
function SubCirugiasDemandadas() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [datos,   setDatos]   = useState(null);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.gerencialCirugias({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const porProcedimiento = datos?.porProcedimiento || [];
  const porCirujano      = datos?.porCirujano || [];
  const maxProc = Math.max(...porProcedimiento.map(p=>p.total_programadas), 1);
  const totalProgramadas = porProcedimiento.reduce((s,p)=>s+Number(p.total_programadas),0);
  const totalRealizadas  = porProcedimiento.reduce((s,p)=>s+Number(p.realizadas),0);

  const imprimirPDF = () => {
    const filasProc = porProcedimiento.map(p=>`<tr>
      <td>${p.nombre_cirugia}</td>
      <td style="text-align:center">${p.total_programadas}</td>
      <td style="text-align:center">${p.realizadas}</td>
      <td style="text-align:center">${p.reprogramadas}</td>
      <td style="text-align:center;font-weight:700">${p.pct_realizadas}%</td>
    </tr>`).join('');
    imprimirHTML(`
      <h2>Cirugías más demandadas</h2>
      <p style="color:#6c757d;margin-bottom:12px">${fmt(desde)} al ${fmt(hasta)} · ${totalProgramadas} cirugías programadas</p>
      <table><thead><tr>
        <th>Procedimiento</th><th>Programadas</th><th>Realizadas</th><th>Reprogramadas</th><th>% Realizadas</th>
      </tr></thead><tbody>${filasProc}</tbody></table>
    `, `Cirugias_Demandadas_${desde}_${hasta}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Cirugías más demandadas</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {porProcedimiento.length>0 && <button style={S.btnNar} onClick={imprimirPDF}>🖨 PDF</button>}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && porProcedimiento.length===0 && (
        <p style={{ color:C.gris,fontSize:'13px' }}>Sin cirugías programadas en este período.</p>
      )}

      {!cargando && porProcedimiento.length>0 && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'16px' }}>
            {[
              { label:'Procedimientos distintos', valor:porProcedimiento.length, color:C.azul   },
              { label:'Total programadas',        valor:totalProgramadas,        color:C.naranja},
              { label:'Realizadas',                valor:totalRealizadas,         color:C.verde  },
            ].map(k=>(
              <div key={k.label} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{k.label}</div>
                <div style={{ fontSize:'16px', fontWeight:'800', color:k.color }}>{k.valor}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize:'11px', fontWeight:'700', color:C.gris, marginBottom:'8px', textTransform:'uppercase' }}>
            Por procedimiento
          </div>
          {porProcedimiento.map(p=>(
            <div key={p.nombre_cirugia} style={{ display:'flex', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #eef1f6' }}>
              <div style={{ minWidth:'200px', fontWeight:'600' }}>{p.nombre_cirugia}</div>
              <div style={S.barraWrap}>
                <div style={S.barra((p.total_programadas/maxProc)*100, C.morado)} />
              </div>
              <span style={{ minWidth:'130px', textAlign:'right', fontSize:'12px' }}>
                <strong style={{ color:C.azul }}>{p.total_programadas}</strong> prog. ·{' '}
                <strong style={{ color:C.verde }}>{p.realizadas}</strong> realiz. ({p.pct_realizadas}%)
              </span>
            </div>
          ))}

          <div style={{ fontSize:'11px', fontWeight:'700', color:C.gris, margin:'20px 0 8px', textTransform:'uppercase' }}>
            Por cirujano
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>{['Cirujano','Programadas','Realizadas'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {porCirujano.map((c,i)=>(
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight:'600' }}>{c.cirujano}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{c.total_programadas}</td>
                    <td style={{ ...S.td, textAlign:'center', color:C.verde, fontWeight:'700' }}>{c.realizadas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── G.3 Rentabilidad por tipo de ingreso ─────────────────────
function SubRentabilidadTipo() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [datos,   setDatos]   = useState([]);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.gerencialRentabilidad({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const totalGeneral = datos.reduce((s,d)=>s+Number(d.total_facturado),0);
  const maxTotal = Math.max(...datos.map(d=>d.total_facturado), 1);

  const COLS_CSV = [
    { key:'tipo_ingreso',     label:'Tipo de ingreso'   },
    { key:'facturas',         label:'Facturas'          },
    { key:'subtotal',         label:'Subtotal ($)'      },
    { key:'total_facturado',  label:'Total facturado ($)'},
    { key:'ticket_promedio',  label:'Ticket promedio ($)'},
    { key:'total_seguro',     label:'Total seguro ($)'  },
    { key:'total_copago',     label:'Total copago ($)'  },
  ];

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Rentabilidad por tipo de ingreso</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {datos.length>0 && (
            <button style={S.btnVerde} onClick={()=>exportarCSV(datos,COLS_CSV,`Rentabilidad_${desde}_${hasta}`)}>⬇ Excel</button>
          )}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && datos.length===0 && (
        <p style={{ color:C.gris,fontSize:'13px' }}>Sin facturas en este período.</p>
      )}

      {!cargando && datos.length>0 && (
        <>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>Total facturado del período</span>
            <span style={{ color:'#fff', fontSize:'20px', fontWeight:'800' }}>{mon(totalGeneral)}</span>
          </div>

          <div style={{ marginTop:'16px' }}>
            {datos.map(d=>(
              <div key={d.tipo_ingreso} style={{ display:'flex', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #eef1f6' }}>
                <div style={{ minWidth:'160px', fontWeight:'600' }}>{d.tipo_ingreso}</div>
                <div style={S.barraWrap}>
                  <div style={S.barra((d.total_facturado/maxTotal)*100, C.verde)} />
                </div>
                <span style={{ fontWeight:'700', color:C.azul, minWidth:'90px', textAlign:'right' }}>
                  {mon(d.total_facturado)}
                </span>
              </div>
            ))}
          </div>

          <div style={{ overflowX:'auto', marginTop:'16px' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Tipo de ingreso','Facturas','Ticket prom.','Seguro','Copago','Total'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map(d=>(
                  <tr key={d.tipo_ingreso}>
                    <td style={{ ...S.td, fontWeight:'600' }}>{d.tipo_ingreso}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{d.facturas}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(d.ticket_promedio)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(d.total_seguro)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(d.total_copago)}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(d.total_facturado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── G.4 Ocupación de habitaciones ────────────────────────────
function SubOcupacionHabitaciones() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [datos,   setDatos]   = useState(null);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.gerencialOcupacion({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const ocupacionActual = datos?.ocupacionActual || [];
  const ingresosPorTipo = datos?.ingresosPorTipo || [];

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Ocupación de habitaciones</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde (ingresos)</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && ocupacionActual.length===0 && (
        <p style={{ color:C.gris,fontSize:'13px' }}>Sin datos de habitaciones.</p>
      )}

      {!cargando && ocupacionActual.length>0 && (
        <>
          <div style={{ fontSize:'11px', fontWeight:'700', color:C.gris, marginBottom:'8px', textTransform:'uppercase' }}>
            Ocupación actual por tipo
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'12px', marginBottom:'20px' }}>
            {ocupacionActual.map(o=>(
              <div key={o.tipo} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${C.azul}` }}>
                <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{o.tipo}</div>
                <div style={{ fontSize:'18px', fontWeight:'800', color:C.azul }}>{o.ocupadas}/{o.total}</div>
                <div style={{ marginTop:'6px' }}>
                  <div style={S.barraWrap}>
                    <div style={S.barra(o.pct_ocupacion, o.pct_ocupacion>80?C.rojo:o.pct_ocupacion>50?C.naranja:C.verde)} />
                  </div>
                </div>
                <div style={{ fontSize:'11px', color:C.gris, marginTop:'4px' }}>{o.pct_ocupacion}% ocupación</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize:'11px', fontWeight:'700', color:C.gris, marginBottom:'8px', textTransform:'uppercase' }}>
            Ingresos por tipo de habitación ({fmt(desde)} – {fmt(hasta)})
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Tipo','Admisiones c/cargo','Días estancia prom.','Total facturado'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ingresosPorTipo.map(r=>(
                  <tr key={r.tipo}>
                    <td style={{ ...S.td, fontWeight:'600' }}>{r.tipo}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{r.admisiones_con_cargo}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{r.dias_estancia_promedio ?? '—'}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(r.total_facturado_habitacion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── G.5 Top servicios facturados ─────────────────────────────
function SubTopServicios() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [tipo,    setTipo]    = useState('');
  const [datos,   setDatos]   = useState(null);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const TIPOS = ['','Habitacion','Medicamento','Procedimiento','Examen','Honorario','Insumo','Otro'];

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.gerencialTopServicios({ desde, hasta, tipo: tipo||undefined });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const porTipo       = datos?.porTipo || [];
  const topServicios  = datos?.topServicios || [];
  const maxServ = Math.max(...topServicios.map(s=>s.total_facturado), 1);

  const COLS_CSV = [
    { key:'tipo',             label:'Categoría'        },
    { key:'descripcion',      label:'Servicio'         },
    { key:'veces_facturado',  label:'Veces facturado'  },
    { key:'unidades_totales', label:'Unidades totales'  },
    { key:'total_facturado',  label:'Total facturado ($)'},
  ];

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Top servicios facturados</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Categoría</label>
            <select style={S.input} value={tipo} onChange={e=>setTipo(e.target.value)}>
              {TIPOS.map(t=><option key={t} value={t}>{t||'Todas'}</option>)}
            </select>
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {topServicios.length>0 && (
            <button style={S.btnVerde} onClick={()=>exportarCSV(topServicios,COLS_CSV,`TopServicios_${desde}_${hasta}`)}>⬇ Excel</button>
          )}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center',color:C.gris,padding:'20px' }}>Generando...</div>}
      {!cargando && buscado && topServicios.length===0 && (
        <p style={{ color:C.gris,fontSize:'13px' }}>Sin cargos en este período.</p>
      )}

      {!cargando && topServicios.length>0 && (
        <>
          <div style={{ fontSize:'11px', fontWeight:'700', color:C.gris, marginBottom:'8px', textTransform:'uppercase' }}>
            Totales por categoría
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:'10px', marginBottom:'20px' }}>
            {porTipo.map(p=>(
              <div key={p.tipo} style={{ background:'#f8fafd', borderRadius:'8px', padding:'10px 12px', borderLeft:`4px solid ${TIPO_COLOR[p.tipo]||C.azul}` }}>
                <div style={{ fontSize:'10px', color:C.gris, marginBottom:'2px' }}>{p.tipo}</div>
                <div style={{ fontSize:'14px', fontWeight:'800', color:TIPO_COLOR[p.tipo]||C.azul }}>{mon(p.total_facturado)}</div>
                <div style={{ fontSize:'10px', color:C.gris }}>{p.cantidad_cargos} cargos</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize:'11px', fontWeight:'700', color:C.gris, marginBottom:'8px', textTransform:'uppercase' }}>
            Top 30 servicios individuales
          </div>
          {topServicios.slice(0,10).map((s,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', padding:'7px 0', borderBottom:'1px solid #eef1f6' }}>
              <span style={S.badge(TIPO_COLOR[s.tipo]||C.azul)}>{s.tipo}</span>
              <div style={{ flex:1, marginLeft:'10px', fontSize:'13px' }}>{s.descripcion}</div>
              <div style={{ ...S.barraWrap, maxWidth:'120px' }}>
                <div style={S.barra((s.total_facturado/maxServ)*100, TIPO_COLOR[s.tipo]||C.azul)} />
              </div>
              <span style={{ fontWeight:'700', color:C.azul, minWidth:'80px', textAlign:'right', marginLeft:'8px' }}>
                {mon(s.total_facturado)}
              </span>
            </div>
          ))}

          <div style={{ overflowX:'auto', marginTop:'16px' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Categoría','Servicio','Veces facturado','Unidades','Total facturado'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topServicios.map((s,i)=>(
                  <tr key={i}>
                    <td style={S.td}><span style={S.badge(TIPO_COLOR[s.tipo]||C.azul)}>{s.tipo}</span></td>
                    <td style={S.td}>{s.descripcion}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{s.veces_facturado}</td>
                    <td style={{ ...S.td, textAlign:'center' }}>{s.unidades_totales}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(s.total_facturado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  3. FACTURACIÓN POR RANGO
// ════════════════════════════════════════════════════════════
function SeccionFacturacion() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [datos,   setDatos]   = useState([]);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.facturacionRango({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const totalGeneral  = datos.reduce((s,r) => s+Number(r.total),0);
  const totalSeguro   = datos.reduce((s,r) => s+Number(r.seguro_monto),0);
  const totalCopago   = datos.reduce((s,r) => s+Number(r.copago),0);

  const COLS_CSV = [
    { key:'numero',       label:'N° Factura'   },
    { key:'emitida_en',   label:'Fecha/Hora'   },
    { key:'paciente',     label:'Paciente'     },
    { key:'dui',          label:'DUI'          },
    { key:'tipo_ingreso', label:'Tipo ingreso' },
    { key:'subtotal',     label:'Subtotal ($)' },
    { key:'iva_monto',    label:'IVA ($)'      },
    { key:'seguro_monto', label:'Seguro ($)'   },
    { key:'copago',       label:'Copago ($)'   },
    { key:'total',        label:'Total ($)'    },
    { key:'estado',       label:'Estado'       },
    { key:'cajero',       label:'Cajero'       },
  ];

  const imprimirPDF = () => {
    const filas = datos.map(r => `
      <tr>
        <td>${r.numero}</td>
        <td>${fmtDT(r.emitida_en)}</td>
        <td>${r.paciente}</td>
        <td>${r.tipo_ingreso}</td>
        <td style="text-align:right">${mon(r.subtotal)}</td>
        <td style="text-align:right">${mon(r.iva_monto)}</td>
        <td style="text-align:right">${mon(r.seguro_monto)}</td>
        <td style="text-align:right;font-weight:700">${mon(r.total)}</td>
        <td>${r.estado}</td>
      </tr>`).join('');
    imprimirHTML(`
      <h2>Facturación — ${fmt(desde)} al ${fmt(hasta)}</h2>
      <p style="color:#6c757d;margin-bottom:12px">${datos.length} facturas</p>
      <table>
        <thead><tr>
          <th>N° Factura</th><th>Fecha</th><th>Paciente</th><th>Tipo</th>
          <th>Subtotal</th><th>IVA</th><th>Seguro</th><th>Total</th><th>Estado</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
      <div class="total-box">
        <span>Total período · Seguro: ${mon(totalSeguro)} · Copago: ${mon(totalCopago)}</span>
        <span>${mon(totalGeneral)}</span>
      </div>
    `, `Facturación ${desde} ${hasta}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Facturación por rango</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {datos.length>0 && <>
            <button style={S.btnVerde} onClick={() => exportarCSV(datos, COLS_CSV, `Facturacion_${desde}_${hasta}`)}>⬇ Excel</button>
            <button style={S.btnNar}   onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Generando reporte...</div>}
      {!cargando && buscado && datos.length === 0 && <p style={{ color:C.gris, fontSize:'13px' }}>Sin facturas en este período.</p>}

      {!cargando && datos.length > 0 && (
        <>
          {/* KPIs rápidos */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }}>
            {[
              { label:'Facturas',       valor:datos.length,         color:C.azul   },
              { label:'Total período',  valor:mon(totalGeneral),    color:C.verde  },
              { label:'Total seguros',  valor:mon(totalSeguro),     color:C.naranja},
              { label:'Total copago',   valor:mon(totalCopago),     color:C.morado },
            ].map(k => (
              <div key={k.label} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{k.label}</div>
                <div style={{ fontSize:'16px', fontWeight:'800', color:k.color }}>{k.valor}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['N° Factura','Fecha','Paciente','DUI','Tipo','Subtotal','IVA','Seguro','Copago','Total','Estado','Cajero'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map((r,i) => (
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, whiteSpace:'nowrap' }}>{r.numero}</td>
                    <td style={{ ...S.td, whiteSpace:'nowrap', fontSize:'11px' }}>{fmtDT(r.emitida_en)}</td>
                    <td style={S.td}>{r.paciente}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.dui||'—'}</td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{r.tipo_ingreso}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.subtotal)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.iva_monto)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.seguro_monto)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.copago)}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(r.total)}</td>
                    <td style={S.td}>
                      <span style={S.badge(r.estado==='Pagada'?C.verde:r.estado==='Anulada'?C.rojo:C.naranja)}>
                        {r.estado}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.cajero}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>{datos.length} facturas · {fmt(desde)} — {fmt(hasta)}</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{mon(totalGeneral)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  4. CONTABILIDAD — Caja, Libro de ventas, CxC, Anulaciones
// ════════════════════════════════════════════════════════════
function SeccionContabilidad({ usuario }) {
  const [sub, setSub] = useState('caja');
  const SUBTABS = [
    { id:'caja',      label:'💵 Caja diaria'      },
    { id:'ventas',    label:'📘 Libro de ventas'  },
    { id:'cxc',       label:'⏳ Cuentas por cobrar' },
    { id:'anuladas',  label:'🚫 Anulaciones'      },
    { id:'resumen',   label:'📊 Resumen mensual'  },
  ];

  return (
    <div>
      <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
        {SUBTABS.map(t => (
          <button key={t.id}
            style={{ ...S.tab(sub===t.id), padding:'7px 14px', fontSize:'12px' }}
            onClick={() => setSub(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'caja'     && <SubCajaDiaria />}
      {sub === 'ventas'   && <SubLibroVentas />}
      {sub === 'cxc'      && <SubCuentasPorCobrar />}
      {sub === 'anuladas' && <SubAnulaciones />}
      {sub === 'resumen'  && <SubResumenMensual />}
    </div>
  );
}

// ── 4.1 Caja diaria ──────────────────────────────────────────
function SubCajaDiaria() {
  const [fecha,    setFecha]    = useState(hoy());
  const [datos,    setDatos]    = useState(null);
  const [cargando, setCargando] = useState(false);
  const [buscado,  setBuscado]  = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.cajaDia({ fecha });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const COLS_CSV = [
    { key:'registrado_en',  label:'Fecha/Hora'   },
    { key:'tipo_pago',      label:'Tipo de pago'  },
    { key:'forma_pago',     label:'Forma de pago' },
    { key:'monto',          label:'Monto ($)'     },
    { key:'paciente',       label:'Paciente'      },
    { key:'factura_numero', label:'N° Factura'    },
    { key:'referencia',     label:'Referencia'    },
    { key:'cajero',         label:'Cajero'        },
  ];

  const imprimirPDF = () => {
    if (!datos) return;
    const filasResumen = datos.porFormaPago.map(r => `
      <tr><td>${r.forma_pago}</td><td style="text-align:right">${r.cantidad}</td>
          <td style="text-align:right;font-weight:700">${mon(r.total)}</td></tr>`).join('');
    const filasDetalle = datos.detalle.map(r => `
      <tr><td>${fmtDT(r.registrado_en)}</td><td>${r.paciente}</td><td>${r.tipo_pago}</td>
          <td>${r.forma_pago}</td><td>${r.factura_numero||'—'}</td>
          <td style="text-align:right;font-weight:700">${mon(r.monto)}</td></tr>`).join('');
    imprimirHTML(`
      <h2>Caja diaria — ${fmt(fecha)}</h2>
      <p style="color:#6c757d;margin-bottom:12px">Resumen por forma de pago</p>
      <table><thead><tr><th>Forma de pago</th><th>Cantidad</th><th>Total</th></tr></thead>
        <tbody>${filasResumen}</tbody></table>
      <div class="total-box"><span>Total del día</span><span>${mon(datos.totalDia)}</span></div>
      <p style="color:#6c757d;margin:16px 0 8px">Detalle de movimientos</p>
      <table><thead><tr><th>Hora</th><th>Paciente</th><th>Tipo</th><th>Forma</th><th>Factura</th><th>Monto</th></tr></thead>
        <tbody>${filasDetalle}</tbody></table>
    `, `Caja diaria ${fecha}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Caja diaria — ingresos por forma de pago</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Fecha</label>
            <input type="date" style={S.input} value={fecha} onChange={e=>setFecha(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {datos && datos.detalle.length>0 && <>
            <button style={S.btnVerde} onClick={() => exportarCSV(datos.detalle, COLS_CSV, `Caja_${fecha}`)}>⬇ Excel</button>
            <button style={S.btnNar}   onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Generando reporte...</div>}
      {!cargando && buscado && datos && datos.detalle.length === 0 && (
        <p style={{ color:C.gris, fontSize:'13px' }}>Sin movimientos de caja en esta fecha.</p>
      )}

      {!cargando && datos && datos.detalle.length > 0 && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:`repeat(${datos.porFormaPago.length||1},1fr)`, gap:'12px', marginBottom:'16px' }}>
            {datos.porFormaPago.map(r => (
              <div key={r.forma_pago} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${C.verde}` }}>
                <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{r.forma_pago} ({r.cantidad})</div>
                <div style={{ fontSize:'16px', fontWeight:'800', color:C.verde }}>{mon(r.total)}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Hora','Paciente','Tipo','Forma de pago','Factura','Referencia','Cajero','Monto'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.detalle.map((r,i) => (
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td, whiteSpace:'nowrap', fontSize:'11px' }}>{fmtDT(r.registrado_en)}</td>
                    <td style={S.td}>{r.paciente}</td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{r.tipo_pago}</td>
                    <td style={S.td}><span style={S.badge(C.azul)}>{r.forma_pago}</span></td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.factura_numero||'—'}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.referencia||'—'}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.cajero}</td>
                    <td style={{ ...S.td, textAlign:'right', fontWeight:'700', color:C.azul }}>{mon(r.monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>{datos.detalle.length} movimientos · {fmt(fecha)}</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{mon(datos.totalDia)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── 4.2 Libro de ventas (IVA) ────────────────────────────────
function SubLibroVentas() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [datos,   setDatos]   = useState([]);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.libroVentas({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  const totalSubtotal = datos.reduce((s,r)=>s+Number(r.subtotal),0);
  const totalIva       = datos.reduce((s,r)=>s+Number(r.iva_monto),0);
  const totalGeneral   = datos.reduce((s,r)=>s+Number(r.total),0);

  const COLS_CSV = [
    { key:'numero',         label:'N° Factura'  },
    { key:'emitida_en',     label:'Fecha'       },
    { key:'paciente',       label:'Paciente'    },
    { key:'dui',            label:'DUI'         },
    { key:'subtotal',       label:'Subtotal ($)'},
    { key:'iva_porcentaje', label:'IVA %'        },
    { key:'iva_monto',      label:'IVA ($)'      },
    { key:'descuento_total',label:'Descuento ($)'},
    { key:'total',          label:'Total ($)'    },
    { key:'estado',         label:'Estado'       },
    { key:'emitida_por',    label:'Emitida por'  },
  ];

  const imprimirPDF = () => {
    const filas = datos.map(r => `
      <tr><td>${r.numero}</td><td>${fmt(r.emitida_en?.slice(0,10))}</td><td>${r.paciente}</td>
          <td>${r.dui||'—'}</td><td style="text-align:right">${mon(r.subtotal)}</td>
          <td style="text-align:right">${mon(r.iva_monto)}</td>
          <td style="text-align:right;font-weight:700">${mon(r.total)}</td><td>${r.estado}</td></tr>`).join('');
    imprimirHTML(`
      <h2>Libro de ventas — ${fmt(desde)} al ${fmt(hasta)}</h2>
      <p style="color:#6c757d;margin-bottom:12px">${datos.length} facturas · Base para declaración de IVA</p>
      <table><thead><tr>
        <th>N° Factura</th><th>Fecha</th><th>Paciente</th><th>DUI</th>
        <th>Subtotal</th><th>IVA</th><th>Total</th><th>Estado</th>
      </tr></thead><tbody>${filas}</tbody></table>
      <div class="total-box">
        <span>Subtotal: ${mon(totalSubtotal)} · IVA débito fiscal: ${mon(totalIva)}</span>
        <span>${mon(totalGeneral)}</span>
      </div>
    `, `LibroVentas_${desde}_${hasta}`);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Libro de ventas — IVA débito fiscal</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
          {datos.length>0 && <>
            <button style={S.btnVerde} onClick={() => exportarCSV(datos, COLS_CSV, `LibroVentas_${desde}_${hasta}`)}>⬇ Excel</button>
            <button style={S.btnNar}   onClick={imprimirPDF}>🖨 PDF</button>
          </>}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Generando reporte...</div>}
      {!cargando && buscado && datos.length === 0 && <p style={{ color:C.gris, fontSize:'13px' }}>Sin facturas en este período.</p>}

      {!cargando && datos.length > 0 && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'16px' }}>
            {[
              { label:'Subtotal período',     valor:mon(totalSubtotal), color:C.azul   },
              { label:'IVA débito fiscal',    valor:mon(totalIva),      color:C.naranja},
              { label:'Total facturado',      valor:mon(totalGeneral),  color:C.verde  },
            ].map(k => (
              <div key={k.label} style={{ background:'#f8fafd', borderRadius:'8px', padding:'12px 14px', borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:'11px', color:C.gris, marginBottom:'4px' }}>{k.label}</div>
                <div style={{ fontSize:'16px', fontWeight:'800', color:k.color }}>{k.valor}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['N° Factura','Fecha','Paciente','DUI','Subtotal','IVA','Descuento','Total','Estado','Emitida por'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map((r,i) => (
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, whiteSpace:'nowrap' }}>{r.numero}</td>
                    <td style={{ ...S.td, whiteSpace:'nowrap', fontSize:'11px' }}>{fmt(r.emitida_en?.slice(0,10))}</td>
                    <td style={S.td}>{r.paciente}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.dui||'—'}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.subtotal)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.iva_monto)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.descuento_total)}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(r.total)}</td>
                    <td style={S.td}>
                      <span style={S.badge(r.estado==='Pagada'?C.verde:r.estado==='Anulada'?C.rojo:C.naranja)}>
                        {r.estado}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.emitida_por}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>{datos.length} facturas · {fmt(desde)} — {fmt(hasta)}</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{mon(totalGeneral)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── 4.3 Cuentas por cobrar ───────────────────────────────────
function SubCuentasPorCobrar() {
  const [datos,    setDatos]    = useState([]);
  const [cargando, setCargando] = useState(false);
  const [buscado,  setBuscado]  = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.cuentasPorCobrar();
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  useEffect(() => { cargar(); }, []);

  const totalPendiente = datos.reduce((s,r)=>s+Number(r.saldo_acumulado),0);

  const COLS_CSV = [
    { key:'admision_id',     label:'Admisión #'   },
    { key:'paciente',        label:'Paciente'     },
    { key:'dui',             label:'DUI'          },
    { key:'fecha_ingreso',   label:'Fecha ingreso'},
    { key:'tipo_ingreso',    label:'Tipo ingreso' },
    { key:'habitacion',      label:'Habitación'   },
    { key:'dias_abierta',    label:'Días abierta' },
    { key:'saldo_acumulado', label:'Saldo ($)'    },
  ];

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Cuentas por cobrar — admisiones activas sin facturar</span>
        <div style={{ display:'flex', gap:'8px' }}>
          <button style={S.btnAzul} onClick={cargar}>🔄 Actualizar</button>
          {datos.length>0 && (
            <button style={S.btnVerde} onClick={() => exportarCSV(datos, COLS_CSV, `CuentasPorCobrar_${hoy()}`)}>⬇ Excel</button>
          )}
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Cargando...</div>}
      {!cargando && buscado && datos.length === 0 && (
        <p style={{ color:C.verde, fontSize:'13px' }}>✓ No hay cuentas pendientes de facturar.</p>
      )}

      {!cargando && datos.length > 0 && (
        <>
          <div style={{ overflowX:'auto' }}>
            <table style={S.tabla}>
              <thead>
                <tr>
                  {['Admisión','Paciente','DUI','Ingreso','Tipo','Habitación','Días abierta','Saldo acumulado'].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map((r,i) => (
                  <tr key={i}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul }}>#{r.admision_id}</td>
                    <td style={S.td}>{r.paciente}</td>
                    <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.dui||'—'}</td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{fmt(r.fecha_ingreso?.slice(0,10))}</td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{r.tipo_ingreso}</td>
                    <td style={{ ...S.td, fontSize:'11px' }}>{r.habitacion||'—'}</td>
                    <td style={S.td}>
                      <span style={S.badge(r.dias_abierta>7?C.rojo:r.dias_abierta>3?C.naranja:C.gris)}>
                        {r.dias_abierta} día(s)
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign:'right', fontWeight:'700', color:C.azul }}>{mon(r.saldo_acumulado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>{datos.length} cuenta(s) abierta(s) sin facturar</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{mon(totalPendiente)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── 4.4 Anulaciones ──────────────────────────────────────────
function SubAnulaciones() {
  const [desde,   setDesde]   = useState(primerDiaMes());
  const [hasta,   setHasta]   = useState(hoy());
  const [datos,   setDatos]   = useState(null);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.anulaciones({ desde, hasta });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Anulaciones — control de auditoría</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={S.campo}>
            <label style={S.label}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={e=>setDesde(e.target.value)} />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={e=>setHasta(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Generando reporte...</div>}

      {!cargando && buscado && datos && (
        <>
          <div style={{ fontSize:'12px', fontWeight:'700', color:C.rojo, textTransform:'uppercase', letterSpacing:'.04em', margin:'4px 0 8px' }}>
            Facturas anuladas ({datos.facturasAnuladas.length})
          </div>
          {datos.facturasAnuladas.length === 0 ? (
            <p style={{ color:C.gris, fontSize:'13px', marginBottom:'16px' }}>Sin facturas anuladas en este período.</p>
          ) : (
            <div style={{ overflowX:'auto', marginBottom:'20px' }}>
              <table style={S.tabla}>
                <thead><tr>{['N° Factura','Fecha','Paciente','Total','Registrado por'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {datos.facturasAnuladas.map((r,i) => (
                    <tr key={i}>
                      <td style={{ ...S.td, fontWeight:'700', color:C.rojo }}>{r.numero}</td>
                      <td style={{ ...S.td, fontSize:'11px' }}>{fmtDT(r.emitida_en)}</td>
                      <td style={S.td}>{r.paciente}</td>
                      <td style={{ ...S.td, textAlign:'right' }}>{mon(r.total)}</td>
                      <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.registrado_por}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ fontSize:'12px', fontWeight:'700', color:C.rojo, textTransform:'uppercase', letterSpacing:'.04em', margin:'4px 0 8px' }}>
            Ítems de cuenta anulados ({datos.itemsAnulados.length})
          </div>
          {datos.itemsAnulados.length === 0 ? (
            <p style={{ color:C.gris, fontSize:'13px' }}>Sin ítems anulados en este período.</p>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={S.tabla}>
                <thead><tr>{['Fecha','Tipo','Descripción','Total','Motivo','Paciente','Registrado por'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {datos.itemsAnulados.map((r,i) => (
                    <tr key={i}>
                      <td style={{ ...S.td, fontSize:'11px' }}>{fmtDT(r.registrado_en)}</td>
                      <td style={S.td}><span style={S.badge(TIPO_COLOR[r.tipo]||C.gris)}>{r.tipo}</span></td>
                      <td style={{ ...S.td, fontSize:'12px' }}>{r.descripcion}</td>
                      <td style={{ ...S.td, textAlign:'right' }}>{mon(r.total)}</td>
                      <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.motivo_anulacion||'—'}</td>
                      <td style={{ ...S.td, fontSize:'11px' }}>{r.paciente}</td>
                      <td style={{ ...S.td, fontSize:'11px', color:C.gris }}>{r.registrado_por}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── 4.5 Resumen mensual contable ─────────────────────────────
function SubResumenMensual() {
  const [anio,    setAnio]    = useState(mesA().anio);
  const [datos,   setDatos]   = useState([]);
  const [cargando,setCargando]= useState(false);
  const [buscado, setBuscado] = useState(false);

  const cargar = async () => {
    setCargando(true); setBuscado(true);
    const r = await api.reportes.resumenMensual({ anio });
    if (r.ok) setDatos(r.data);
    setCargando(false);
  };

  useEffect(() => { cargar(); }, []);

  const totalAnual = datos.reduce((s,r)=>s+Number(r.total),0);
  const ivaAnual   = datos.reduce((s,r)=>s+Number(r.iva_monto),0);

  return (
    <div style={S.card}>
      <div style={S.cardTit}>
        <span>Resumen mensual — {anio}</span>
        <div style={{ display:'flex', gap:'8px', alignItems:'flex-end' }}>
          <div style={S.campo}>
            <label style={S.label}>Año</label>
            <input type="number" style={{ ...S.input, width:'90px' }} value={anio} onChange={e=>setAnio(e.target.value)} />
          </div>
          <button style={S.btnAzul} onClick={cargar}>Generar</button>
        </div>
      </div>

      {cargando && <div style={{ textAlign:'center', color:C.gris, padding:'20px' }}>Generando reporte...</div>}
      {!cargando && buscado && datos.length === 0 && <p style={{ color:C.gris, fontSize:'13px' }}>Sin facturación registrada en {anio}.</p>}

      {!cargando && datos.length > 0 && (
        <>
          <div style={{ overflowX:'auto', marginBottom:'16px' }}>
            <table style={S.tabla}>
              <thead>
                <tr>{['Mes','Facturas','Subtotal','IVA','Seguros','Copago','Total'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {datos.map((r,i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul }}>{MESES[Number(r.mes)-1]}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{r.facturas}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.subtotal)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.iva_monto)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.seguro_monto)}</td>
                    <td style={{ ...S.td, textAlign:'right' }}>{mon(r.copago)}</td>
                    <td style={{ ...S.td, fontWeight:'700', color:C.azul, textAlign:'right' }}>{mon(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={S.totalBox}>
            <span style={{ color:'#fff', fontSize:'13px' }}>Total {anio} · IVA débito fiscal: {mon(ivaAnual)}</span>
            <span style={{ color:'#fff', fontSize:'18px', fontWeight:'800' }}>{mon(totalAnual)}</span>
          </div>
        </>
      )}
    </div>
  );
}

